import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Select, Table, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { StatusMark } from '../../../components/StatusMark';
import { DictDetailModal } from './DictDetailModal';
import { DictTypeModal, type DictTypeFormValues } from './DictTypeModal';
import {
  DICTIONARY_STATUS_LABEL,
  DICTIONARY_STATUS_OPTIONS,
  DICTIONARY_VISIBILITY_LABEL,
  createDictionaryType,
  createDictionaryItem,
  deleteDictionaryItem,
  deleteDictionaryType,
  getDictionaryItems,
  getDictionaryTypes,
  updateDictionaryItem,
  updateDictionaryType,
  type DictGroup,
  type DictItem,
  type DictionaryItem,
  type DictionaryType
} from './dictManageData';

type Filters = {
  name: string;
  code: string;
  status?: string;
};

const emptyFilters: Filters = { name: '', code: '' };

function toDictItem(item: DictionaryItem): DictItem {
  return {
    id: item.id ?? '',
    label: item.label ?? '',
    value: item.value ?? '',
    sort: item.sortOrder ?? 0,
    status: item.status === 'ACTIVE' ? 'normal' : 'disabled',
    remark: item.description ?? '',
    createdAt: '',
    version: item.version
  };
}

function toDictGroup(row: DictionaryType, items: DictItem[]): DictGroup {
  return {
    id: row.id ?? row.code ?? '',
    name: row.name ?? '',
    type: row.code ?? '',
    status: row.status === 'ACTIVE' ? 'normal' : 'disabled',
    remark: row.description ?? '',
    createdAt: '',
    items
  };
}

export function DictManage() {
  const [types, setTypes] = useState<DictionaryType[]>([]);
  const [itemMap, setItemMap] = useState<Record<string, DictItem[]>>({});
  const [itemsLoading, setItemsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<Filters>(emptyFilters);
  const [applied, setApplied] = useState<Filters>(emptyFilters);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<DictionaryType | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const loadTypes = () => {
    setLoading(true);
    return getDictionaryTypes()
      .then((res) => {
        if (res.code === 200 && Array.isArray(res.content)) {
          setTypes(res.content);
          return res.content;
        }
        message.error(res.message || '字典列表加载失败');
        return undefined;
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDictionaryTypes()
      .then((res) => {
        if (cancelled) return;
        if (res.code === 200 && Array.isArray(res.content)) {
          setTypes(res.content);
          return;
        }
        message.error(res.message || '字典列表加载失败');
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadItems = useCallback((typeId: string) => {
    setItemsLoading(true);
    return getDictionaryItems(typeId)
      .then((res) => {
        if (res.code === 200 && Array.isArray(res.content)) {
          const items = res.content;
          setItemMap((prev) => ({ ...prev, [typeId]: items.map(toDictItem) }));
          return;
        }
        message.error(res.message || '字典项加载失败');
      })
      .catch(() => undefined)
      .finally(() => setItemsLoading(false));
  }, []);

  useEffect(() => {
    if (!detailId) return;
    loadItems(detailId);
  }, [detailId, loadItems]);

  const detailGroup = useMemo(() => {
    const row = types.find((item) => (item.id ?? item.code) === detailId);
    if (!row) return null;
    return toDictGroup(row, itemMap[detailId ?? ''] ?? []);
  }, [detailId, itemMap, types]);

  const filtered = useMemo(() => {
    const nameQ = applied.name.trim().toLowerCase();
    const codeQ = applied.code.trim().toLowerCase();
    return types.filter((row) => {
      if (applied.status && row.status !== applied.status) return false;
      if (nameQ && !(row.name ?? '').toLowerCase().includes(nameQ)) return false;
      if (codeQ && !(row.code ?? '').toLowerCase().includes(codeQ)) return false;
      return true;
    });
  }, [applied, types]);

  const openCreate = () => {
    setEditingType(null);
    setTypeModalOpen(true);
  };

  const openEdit = (row: DictionaryType) => {
    setEditingType(row);
    setTypeModalOpen(true);
  };

  const openDetail = (row: DictionaryType) => {
    if (!row.id) {
      message.warning('缺少字典类型编码');
      return;
    }
    setDetailId(row.id);
    setDetailOpen(true);
  };

  const confirmDelete = (row: DictionaryType) => {
    const typeId = row.id;
    const version = row.version;
    if (!typeId) {
      message.error('缺少字典类型编号');
      return;
    }
    if (version == null || version < 0) {
      message.error('缺少版本号，无法删除');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `确定删除字典「${row.name || row.code || ''}」吗？删除后编码不可再用，也不能再作为业务选项。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      centered: true,
      onOk: async () => {
        const res = await deleteDictionaryType(typeId, version);
        if (res.code === 409) {
          message.warning(res.message || '数据已变化，请按最新内容重新确认');
          const latestList = await loadTypes();
          const latest = latestList?.find((item) => item.id === typeId);
          if (latest && latest.status !== 'DELETED') {
            setTimeout(() => confirmDelete(latest), 0);
          }
          return;
        }
        if (res.code !== 200) {
          message.error(res.message || '删除字典类型失败');
          return Promise.reject(new Error('delete failed'));
        }
        message.success(res.message || '字典类型已删除');
        if (detailId === typeId) {
          setDetailOpen(false);
          setDetailId(null);
        }
        await loadTypes();
      }
    });
  };

  const handleTypeSubmit = async (values: DictTypeFormValues) => {
    const description = values.description?.trim() ?? '';
    if (editingType) {
      const typeId = editingType.id;
      const version = editingType.version;
      if (!typeId) {
        message.error('缺少字典类型编号');
        return false;
      }
      if (version == null || version < 0) {
        message.error('缺少版本号，无法更新');
        return false;
      }
      const res = await updateDictionaryType(typeId, {
        code: values.code,
        name: values.name,
        description,
        visibility: values.visibility,
        status: values.status,
        version
      });
      if (res.code !== 200) {
        message.error(res.message || '修改字典类型失败');
        return false;
      }
      message.success(res.message || '字典类型已更新');
      setTypeModalOpen(false);
      setEditingType(null);
      await loadTypes();
      return true;
    }

    const res = await createDictionaryType({
      code: values.code,
      name: values.name,
      visibility: values.visibility,
      status: values.status,
      ...(description ? { description } : {})
    });
    if (res.code !== 200) {
      message.error(res.message || '新增字典类型失败');
      return false;
    }
    message.success(res.message || '字典类型已创建');
    setTypeModalOpen(false);
    setEditingType(null);
    await loadTypes();
    return true;
  };

  const handleChangeItems = (groupId: string, items: DictItem[]) => {
    setItemMap((prev) => ({ ...prev, [groupId]: items }));
  };

  const handleCreateItem = async (item: Pick<DictItem, 'label' | 'value' | 'sort' | 'status' | 'remark'>) => {
    if (!detailId) {
      message.error('缺少字典类型编号');
      return false;
    }
    const res = await createDictionaryItem(detailId, {
      value: item.value,
      label: item.label,
      description: item.remark,
      sortOrder: item.sort,
      status: item.status === 'normal' ? 'ACTIVE' : 'DISABLED'
    });
    if (res.code !== 200) {
      message.error(res.message || '新增字典项失败');
      return false;
    }
    message.success(res.message || '字典项已添加');
    await loadItems(detailId);
    return true;
  };

  const handleDeleteItem = async (item: DictItem) => {
    if (!detailId) {
      message.error('缺少字典类型编号');
      return false;
    }
    if (!item.id) {
      message.error('缺少字典项编号');
      return false;
    }
    if (item.version == null || item.version < 0) {
      message.error('缺少版本号，无法删除');
      return false;
    }
    const res = await deleteDictionaryItem(detailId, item.id, item.version);
    if (res.code !== 200) {
      message.error(res.message || '删除字典项失败');
      return false;
    }
    message.success(res.message || '字典项已删除');
    await loadItems(detailId);
    return true;
  };

  const handleUpdateItem = async (item: DictItem) => {
    if (!detailId) {
      message.error('缺少字典类型编号');
      return false;
    }
    if (!item.id) {
      message.error('缺少字典项编号');
      return false;
    }
    if (item.version == null || item.version < 0) {
      message.error('缺少版本号，无法更新');
      return false;
    }
    const res = await updateDictionaryItem(detailId, item.id, {
      value: item.value,
      label: item.label,
      description: item.remark,
      sortOrder: item.sort,
      status: item.status === 'normal' ? 'ACTIVE' : 'DISABLED',
      version: item.version
    });
    if (res.code !== 200) {
      message.error(res.message || '修改字典项失败');
      return false;
    }
    message.success(res.message || '字典项已更新');
    await loadItems(detailId);
    return true;
  };

  const columns: ColumnsType<DictionaryType> = [
    {
      title: '序号',
      width: 80,
      render: (_, __, index) => index + 1
    },
    {
      title: '字典名称',
      dataIndex: 'name',
      render: (name?: string) => <span className='admin-cell-primary'>{name || '—'}</span>
    },
    {
      title: '字典标识',
      dataIndex: 'code',
      render: (code?: string) => <code className='admin-dict-code'>{code || '—'}</code>
    },
    {
      title: '可见范围',
      dataIndex: 'visibility',
      render: (visibility?: string) => DICTIONARY_VISIBILITY_LABEL[visibility ?? ''] || visibility || '—'
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status?: string) => {
        const label = DICTIONARY_STATUS_LABEL[status ?? ''] || status || '—';
        const tone = status === 'ACTIVE' ? 'success' : status === 'DISABLED' || status === 'DELETED' ? 'muted' : '';
        return <StatusMark tone={tone}>{label}</StatusMark>;
      }
    },
    {
      title: '说明',
      dataIndex: 'description',
      ellipsis: true,
      render: (description?: string) => description || '—'
    },
    {
      title: '版本',
      dataIndex: 'version',
      width: 90,
      render: (version?: number) => (version == null ? '—' : version)
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, row) => (
        <div className='admin-row-actions'>
          <button type='button' className='admin-link-btn' onClick={() => openEdit(row)}>
            编辑
          </button>
          <button type='button' className='admin-link-btn' onClick={() => openDetail(row)}>
            详情
          </button>
          <button
            type='button'
            className='admin-link-btn admin-link-btn--danger'
            onClick={() => confirmDelete(row)}
          >
            删除
          </button>
        </div>
      )
    }
  ];

  return (
    <div className='admin-page-card'>
      <div className='admin-filter-bar admin-pkg-filter-bar'>
        <Input
          allowClear
          className='admin-filter-search'
          placeholder='请输入字典名称'
          value={draft.name}
          onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
          onPressEnter={() => setApplied(draft)}
        />
        <Input
          allowClear
          className='admin-filter-search'
          placeholder='请输入字典标识'
          value={draft.code}
          onChange={(e) => setDraft((prev) => ({ ...prev, code: e.target.value }))}
          onPressEnter={() => setApplied(draft)}
        />
        <Select
          allowClear
          className='admin-filter-select'
          placeholder='字典状态'
          options={DICTIONARY_STATUS_OPTIONS.map((item) => ({ value: item.value, label: item.label }))}
          value={draft.status}
          onChange={(status) => setDraft((prev) => ({ ...prev, status }))}
        />
        <Button type='primary' className='admin-pkg-query-btn' onClick={() => setApplied(draft)}>
          查询
        </Button>
        <Button
          onClick={() => {
            setDraft(emptyFilters);
            setApplied(emptyFilters);
          }}
        >
          重置
        </Button>
      </div>

      <div className='mb-3'>
        <Button type='primary' icon={<PlusOutlined />} className='admin-pkg-create-btn' onClick={openCreate}>
          新增
        </Button>
      </div>

      <Table<DictionaryType>
        rowKey={(row) => row.id || row.code || row.name || ''}
        columns={columns}
        dataSource={filtered}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        scroll={{ x: 1100 }}
        className='admin-user-table'
        size='middle'
      />

      <DictTypeModal
        open={typeModalOpen}
        editing={editingType}
        onClose={() => {
          setTypeModalOpen(false);
          setEditingType(null);
        }}
        onSubmit={handleTypeSubmit}
      />

      <DictDetailModal
        open={detailOpen}
        group={detailGroup}
        onClose={() => {
          setDetailOpen(false);
          setDetailId(null);
        }}
        onChangeItems={handleChangeItems}
        onCreateItem={handleCreateItem}
        onDeleteItem={handleDeleteItem}
        onUpdateItem={handleUpdateItem}
        itemsLoading={itemsLoading}
      />
    </div>
  );
}
